// S44 — Refactor : branché sur les endpoints réels /api/auth/profile-extracted
// Édition, suppression par champ, purge totale. Auto-reload après chaque action.

import React, { useState, useEffect } from 'react';
import { X, Edit3, Check, Trash2 } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

const FIELDS = [
  { key: 'prenom', label: 'Prénom' },
  { key: 'nom', label: 'Nom' },
  { key: 'entreprise', label: 'Entreprise' },
  { key: 'poste', label: 'Poste' },
  { key: 'secteur_activite', label: 'Secteur' },
  { key: 'localisation', label: 'Localisation' },
  { key: 'langue_preferee', label: 'Langue' },
  { key: 'ton_prefere', label: 'Ton préféré' },
  { key: 'contexte_utilisation', label: 'Contexte' },
  { key: 'centres_interet', label: 'Centres d\'intérêt', isArray: true },
  { key: 'competences_declarees', label: 'Compétences', isArray: true },
  { key: 'objectifs', label: 'Objectifs', isArray: true },
];

const ProfileSummary = ({ onClose, onUpdate }) => {
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [loaded, setLoaded] = useState(null);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('oracle_token') || ''}`,
    'Content-Type': 'application/json',
  });

  const load = async () => {
    try {
      const res = await fetch('/api/auth/profile-extracted', { headers: headers() });
      if (res.ok) setLoaded(await res.json());
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const data = loaded?.profile_data || {};
  const score = Math.round((loaded?.completeness_score || 0) * 100);

  const startEdit = (field) => {
    const val = data[field.key];
    setEditValue(field.isArray ? (val || []).join(', ') : (val || ''));
    setEditing(field.key);
  };

  const saveEdit = async (field) => {
    const value = field.isArray
      ? editValue.split(',').map(s => s.trim()).filter(Boolean)
      : editValue.trim();
    const updated = { ...(data || {}) };
    if (field.isArray) {
      if (value.length > 0) updated[field.key] = value;
      else delete updated[field.key];
    } else {
      if (value) updated[field.key] = value;
      else delete updated[field.key];
    }
    try {
      const res = await fetch('/api/auth/profile-extracted', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ profile_data: updated })
      });
      if (res.ok) {
        toast.success('Champ mis à jour');
        setEditing(null);
        setEditValue('');
        await load();
        if (onUpdate) onUpdate();
      } else toast.error('Erreur mise à jour');
    } catch { toast.error('Erreur mise à jour'); }
  };

  const deleteField = async (field) => {
    try {
      const res = await fetch(`/api/auth/profile-extracted/field/${field.key}`, {
        method: 'DELETE', headers: headers(),
      });
      if (res.ok) {
        toast.success('Supprimé');
        await load();
        if (onUpdate) onUpdate();
      } else toast.error('Erreur suppression');
    } catch { toast.error('Erreur suppression'); }
  };

  const purgeAll = async () => {
    const confirmed = await confirmModal(
      'Purger totalement le profil extrait par l\'IA ? Tous les champs remémorés seront effacés.',
      { confirmText: 'Tout purger', danger: true }
    );
    if (!confirmed) return;
    try {
      const res = await fetch('/api/auth/profile-extracted', {
        method: 'DELETE', headers: headers(),
      });
      if (res.ok) {
        toast.success('Profil IA purgé');
        await load();
        if (onUpdate) onUpdate();
      } else toast.error('Erreur purge');
    } catch { toast.error('Erreur purge'); }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Votre profil ITRIIA</h3>
          <button onClick={onClose} style={styles.closeBtn}><X size={18} /></button>
        </div>
        <div style={styles.scoreBar}>
          <div style={styles.scoreLabel}>Complétude : {score}%</div>
          <div style={styles.barBg}>
            <div style={{ ...styles.barFill, width: `${score}%` }} />
          </div>
        </div>
        <div style={styles.body}>
          {FIELDS.map(field => {
            const hasValue = field.isArray ? (data[field.key]?.length > 0) : !!data[field.key];
            return (
              <div key={field.key} style={styles.row}>
                <div style={styles.fieldLabel}>{field.label}</div>
                {editing === field.key ? (
                  <div style={styles.editRow}>
                    <input value={editValue} onChange={e => setEditValue(e.target.value)}
                      style={styles.editInput} autoFocus
                      placeholder={field.isArray ? 'élément 1, élément 2, …' : 'valeur'}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveEdit(field);
                        if (e.key === 'Escape') { setEditing(null); setEditValue(''); }
                      }} />
                    <button onClick={() => saveEdit(field)} style={styles.saveBtn}><Check size={14} /></button>
                    <button onClick={() => { setEditing(null); setEditValue(''); }} style={styles.cancelBtn}><X size={14} /></button>
                  </div>
                ) : (
                  <div style={styles.valueRow}>
                    <span style={styles.fieldValue}>
                      {field.isArray
                        ? (data[field.key]?.length ? data[field.key].join(', ') : '—')
                        : (data[field.key] || '—')}
                    </span>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <button onClick={() => startEdit(field)} title="Éditer" style={styles.editBtn}><Edit3 size={12} /></button>
                      {hasValue && (
                        <button onClick={() => deleteField(field)} title="Oublier ce champ" style={styles.delBtn}><Trash2 size={12} /></button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={styles.footer}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Les infos sont extraites automatiquement de vos conversations
          </span>
          <button onClick={purgeAll} style={styles.purgeBtn}>
            <Trash2 size={12} /> Tout purger
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'var(--overlay-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: 'var(--modal-bg)', border: '1px solid var(--modal-border)', borderRadius: 16, width: 520, maxWidth: '90vw',
    maxHeight: '80vh', overflow: 'hidden', boxShadow: 'var(--shadow-lg)',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 24px', borderBottom: '1px solid var(--card-border)',
  },
  title: { fontSize: 17, fontWeight: 600, color: 'var(--text)', margin: 0 },
  closeBtn: { background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' },
  scoreBar: { padding: '12px 24px', background: 'var(--input-bg)', borderBottom: '1px solid var(--card-border)' },
  scoreLabel: { fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 6 },
  barBg: { height: 6, background: 'var(--profile-bar)', borderRadius: 3 },
  barFill: { height: '100%', background: 'var(--profile-fill)', borderRadius: 3, transition: 'width 0.3s' },
  body: { padding: '16px 24px', overflowY: 'auto', flex: 1 },
  row: { padding: '10px 0', borderBottom: '1px solid var(--card-border)' },
  fieldLabel: { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  valueRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  fieldValue: { fontSize: 14, color: 'var(--text)', flex: 1, minWidth: 0, wordBreak: 'break-word' },
  editBtn: { background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 4, borderRadius: 4 },
  delBtn: { background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 4, borderRadius: 4 },
  editRow: { display: 'flex', gap: 6 },
  editInput: {
    flex: 1, padding: '6px 10px', border: '1px solid #3b82f6', borderRadius: 6,
    fontSize: 14, outline: 'none', background: 'var(--input-bg)', color: 'var(--text)',
  },
  saveBtn: {
    width: 30, height: 30, borderRadius: 6, border: '1px solid #22c55e',
    background: 'rgba(34,197,94,0.12)', color: '#22c55e', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  cancelBtn: {
    width: 30, height: 30, borderRadius: 6, border: '1px solid var(--card-border)',
    background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  footer: {
    padding: '12px 24px', borderTop: '1px solid var(--card-border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  },
  purgeBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
    color: '#ef4444', cursor: 'pointer', transition: 'all 0.15s',
  },
};

export default ProfileSummary;
